import React, { useEffect } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import cx from 'classnames'

import { useTheme } from '@material-ui/core/styles'
import { Button, Grid, IconButton, Typography, Tooltip, makeStyles } from '@material-ui/core'
import { ArrowBack, PinDrop } from '@material-ui/icons'
import CloseIcon from '@material-ui/icons/Close'

import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'

import ShareSpeedDial from '~/components/SpeedDial'
import ImagePlaceholder from '~/components/ImagePlaceholder'
import ButtonBuyOnline from '~/components/ButtonBuyOnline'
import useEcommerce from '~/components/Ecommerce/useEcommerce'
import useViewportSize from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'

import { mountUrl, replaceImagePath } from '~/utils'
import track from '~/track'

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    gap: 8,
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #E0E0E0'
  },
  bar: {
    height: '100%',
    padding: '8px 32px',
    overflow: 'auto',

    '& .simplebar-content': {
      display: 'flex',
      height: '100%',
      overflow: 'auto',
      justifyContent: 'center',
      ...theme.gofind.scrollBar
    }
  },
  content: {
    gap: 10,
    display: 'grid',
    alignItems: 'center',
    flexDirection: 'column',
    justifyItems: 'center',

    [theme.breakpoints.up('md')]: {
      maxWidth: '100%'
    },
    '& > p': {
      margin: '0 auto',
      whiteSpace: 'pre-wrap'
    }
  },
  footer: {
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  buttons: {
    gap: 8,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  productImage: {
    transition: '.3s ease',
    maxWidth: '100%'
  },
  productImageLoading: {
    height: 300,
    filter: 'blur(5px)'
  },
  productName: {
    width: '100%'
  }
}))

function ProductDetails({
  data: { product, disableFind },
  store,
  onClose,
  onFind,
  position,
  config
}) {
  const { openEcommerce, setOpenEcommerce, dataEcommerce, loadingEcommerce, setApiEcommerce } =
    useEcommerce()
  const { getLiteral } = useLiterals()
  const classes = useStyles()
  const history = useHistory()

  const theme = useTheme()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const image = replaceImagePath(product)

  useEffect(() => {
    if (history.location.state) {
      track('cliqueProdutoDetalhes', { category: 'menu', name: product?.name })
    } else {
      track('linkProdutoDetalhes', { name: product?.name })
    }
  }, [product])

  if (!product && config.showContextViewStore) return <Redirect to="/produto/todos" />
  if (!product && !config.showContextViewStore) return <Redirect to="/" />

  return (
    <Grid className={classes.root}>
      <Grid className={classes.title}>
        <Grid style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {isMobile && (
            <Tooltip title={getLiteral('1051')} placement="bottom">
              <IconButton
                aria-label={getLiteral('1051')}
                onClick={onClose}
                color="primary"
                size="small"
              >
                <ArrowBack color="inherit" />
              </IconButton>
            </Tooltip>
          )}

          <Typography variant="h6" className={classes.productName}>
            {product.name}
          </Typography>
        </Grid>

        {!isMobile && (
          <Tooltip title={getLiteral('1251')} placement="bottom">
            <IconButton size="small" aria-label={getLiteral('1251')} onClick={onClose}>
              <CloseIcon color="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Grid>

      <SimpleBar className={classes.bar}>
        <Grid className={classes.content}>
          {image ? (
            <ImagePlaceholder
              src={mountUrl(image, { height: 480 })}
              placeholder={mountUrl(image, { height: 100 })}
            >
              {(src, loading) => (
                <img
                  data-cy="product-image"
                  src={src}
                  alt={product.name}
                  className={cx(classes.productImage, {
                    [classes.productImageLoading]: loading
                  })}
                />
              )}
            </ImagePlaceholder>
          ) : (
            <Grid data-cy="product-without-image">{getLiteral('2030')}</Grid>
          )}

          <Typography data-cy="product-description" variant="body1" gutterBottom>
            {product.description}
          </Typography>

          {isMobile && <ShareSpeedDial product={product} mobile={isMobile} />}
        </Grid>
      </SimpleBar>

      <Grid className={classes.footer}>
        <Grid
          className={classes.buttons}
          {...(isMobile && {
            style: { width: '100%', flexDirection: 'column-reverse', alignItems: 'unset' }
          })}
        >
          {!disableFind && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => onFind()}
              className={classes.buttonAction}
              startIcon={<PinDrop />}
            >
              {getLiteral('2347')}
            </Button>
          )}

          <ButtonBuyOnline
            modal
            product={product}
            store={store}
            position={position}
            openEcommerce={openEcommerce}
            dataEcommerce={dataEcommerce}
            setOpenEcommerce={setOpenEcommerce}
            loadingEcommerce={loadingEcommerce}
            setApiEcommerce={setApiEcommerce}
          />
        </Grid>

        {!isMobile && <ShareSpeedDial product={product} mobile={isMobile} />}
      </Grid>
    </Grid>
  )
}

export default ProductDetails
